/**
 * 验证电子邮箱格式
 */
export function isEmail(value: string): boolean {
  return /^[\w.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value);
}

/**
 * 验证手机格式
 */
export function isMobile(value: string): boolean {
  return /^1([3-9]\d)\d{8}$/.test(value);
}

/**
 * 验证URL格式
 */
export function isUrl(value: string): boolean {
  return /^(https?|ftp|rtsp|mms):\/\/.+/.test(value);
}

/**
 * 验证日期格式
 */
export function isDate(value: any): boolean {
  if (!value) return false;
  // 判断是否数值或者字符串数值(意味着为时间戳)，转为数值，否则new Date无法识别字符串时间戳
  if (isNumber(value)) value = +value;
  return !/Invalid|NaN/.test(new Date(value).toString());
}

/**
 * 验证ISO类型的日期格式
 */
export function isDateIso(value: string): boolean {
  return /^\d{4}[\/-](0?[1-9]|1[0-2])[\/-](0?[1-9]|[12][0-9]|3[01])$/.test(value);
}

/**
 * 验证十进制数字
 */
export function isNumber(value: any): boolean {
  return /^[+-]?(\d+\.?\d*|\.\d+)$/.test(String(value));
}

/**
 * 验证字符串
 */
export function isString(value: any): boolean {
  return typeof value === 'string';
}

/**
 * 验证整数
 */
export function isDigits(value: any): boolean {
  return /^\d+$/.test(String(value));
}

/**
 * 验证身份证号码
 */
export function isIdCard(value: string): boolean {
  return /^[1-9]\d{5}(19|20)\d{2}((0[1-9])|(1[0-2]))(([0|1|2]\d)|3[0-1])\d{3}([0-9Xx])$/.test(value);
}

/**
 * 是否车牌号
 */
export function isCarNo(value: string): boolean {
  // 新能源车牌
  const xreg = /^[\u4e00-\u9fa5]{1}[A-Z]{1}(([0-9]{5}[DF])|([DF][A-HJ-NP-Z0-9][0-9]{4}))$/;
  // 旧车牌
  const creg = /^[\u4e00-\u9fa5]{1}[A-Z]{1}[A-HJ-NP-Z0-9]{4}[A-HJ-NP-Z0-9挂学警港澳]{1}$/;
  if (value.length === 7) return creg.test(value);
  if (value.length === 8) return xreg.test(value);
  return false;
}

/**
 * 金额,只允许2位小数
 */
export function isAmount(value: string): boolean {
  return /^([1-9]\d*(,\d{3})*(\.\d{1,2})?|0\.\d{1,2})$/.test(value);
}

/**
 * 中文
 */
export function isChinese(value: string): boolean {
  return /^[\u4e00-\u9fa5]+$/gi.test(value);
}

/**
 * 只能输入字母
 */
export function isLetter(value: string): boolean {
  return /^[a-zA-Z]*$/.test(value);
}

/**
 * 只能是字母或者数字
 */
export function isEnOrNum(value: string): boolean {
  return /^[0-9a-zA-Z]*$/.test(value);
}

/**
 * 验证是否包含某个值
 */
export function isContains(value: string, param: string): boolean {
  return value.indexOf(param) >= 0;
}

/**
 * 验证一个值范围[min, max]
 */
export function isRange(value: number, param: [number, number]): boolean {
  return value >= param[0] && value <= param[1];
}

/**
 * 验证一个长度范围[min, max]
 */
export function isRangeLength(value: string | any[], param: [number, number]): boolean {
  return value.length >= param[0] && value.length <= param[1];
}

/**
 * 是否固定电话
 */
export function isLandline(value: string): boolean {
  return /^\d{3,4}-\d{7,8}(-\d{3,4})?$/.test(value);
}

/**
 * 是否json字符串
 */
export function isJsonString(value: any): boolean {
  if (typeof value !== 'string') return false;
  try {
    const obj = JSON.parse(value);
    return typeof obj === 'object' && obj !== null;
  } catch {
    return false;
  }
}

/**
 * 是否数组
 */
export function isArray(value: any): boolean {
  return Array.isArray(value);
}

/**
 * 是否对象
 */
export function isObject(value: any): boolean {
  return Object.prototype.toString.call(value) === '[object Object]';
}

/**
 * 是否短信验证码
 */
export function isCode(value: string, len = 6): boolean {
  return new RegExp(`^\\d{${len}}$`).test(value);
}

/**
 * 是否函数方法
 * @param {Object} value
 */
export function isFunc(value: any): boolean {
  return typeof value === 'function';
}

/**
 * 是否promise对象
 * @param {Object} value
 */
export function isPromise(value: any): boolean {
  return isObject(value) && isFunc(value.then) && isFunc(value.catch);
}

/** 是否图片格式
 * @param {Object} value
 */
export function isImage(value: string): boolean {
  const newValue = value.split('?')[0];
  return /\.(jpeg|jpg|gif|png|svg|webp|jfif|bmp|dpg)$/i.test(newValue);
}

/**
 * 是否视频格式
 * @param {Object} value
 */
export function isVideo(value: string): boolean {
  return /\.(mp4|mpg|mpeg|dat|asf|avi|rm|rmvb|mov|wmv|flv|mkv|m3u8)$/i.test(value);
}

/**
 * 是否为正则对象
 * @param o
 * @return {Boolean}
 */
export function isRegExp(o: any): boolean {
  return o && Object.prototype.toString.call(o) === '[object RegExp]';
}
